import React, { forwardRef, Ref, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Modal from 'react-modal';
import { css } from 'emotion';

import { Vehicle } from '@vromo/models';
import { Job } from '@vromo/models';
import { when } from 'mobx';
import { fadeIn, jackInTheBox } from '@vromo/ui/styles/animated';
import Color from 'color';
import range from 'lodash/range';
import styled from '@emotion/styled';
import Tracking from '@vromo/analytics';
import { Graph } from '@vromo/api/graph';

const InlineSubmit = styled.button`
    font-family: 'aktiv-grotesk', 'Helvetica Neue', 'Helvetica', 'Arial', 'Open Sans', sans-serif;
    margin-top: 14px;
    padding: 5px 14px;
    align-self: center;
    background-color: ${({ theme }) => theme.accent};
    border: none;
    color: ${({ theme }) => (Color(theme.accent).isLight() ? '#111111' : 'white')};
    border-radius: 1000px;
    font-size: 12px;
    cursor: pointer;
`;

const PopupSubmit = styled.button`
    font-family: 'aktiv-grotesk', 'Helvetica Neue', 'Helvetica', 'Arial', 'Open Sans', sans-serif;
    margin-top: 14px;
    padding: 5px 14px;
    align-self: center;
    background-color: ${({ theme }) => theme.background};
    border: none;
    color: ${({ theme }) => (Color(theme.background).isLight() ? '#111111' : 'white')};
    border-radius: 1000px;
    font-size: 12px;
    cursor: pointer;
`;

interface WidgetProps {
    widget: any;
    theme: any;
    vehicle: Vehicle;
    job: Job;
    rating: number;
    setRating: (rating: number) => void;
    submit: () => void;
    comment: string;
    setComment: (comment: string) => void;
    host: string;
    auth: string;
}

const Popup = forwardRef(function Popup(
    { widget, theme, job, rating, setRating, submit, comment, setComment }: WidgetProps,
    ref: Ref<HTMLDivElement>
) {
    const [closed, setClosed] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        return when(
            () => {
                switch (widget.settings.when) {
                    case 'dispatched':
                        return job.status === 'dispatched';
                    case 'underway':
                        return job.status === 'underway' && job.progress === 0;
                    case 'completed':
                        return job.status === 'completed';
                    case 'cancelled':
                        return job.status === 'cancelled';
                    case 'task0':
                        return job.progress === 1 && job.status === 'underway';
                    case 'task1':
                        return job.progress === 2 && job.status === 'underway';
                    case 'task2':
                        return job.progress === 3 && job.status === 'underway';
                    case 'task3':
                        return job.progress === 4 && job.status === 'underway';
                    default:
                        return false;
                }
            },
            () => {
                setOpen(true);
            }
        );
    }, []);

    return (
        <Modal
            isOpen={open && !closed}
            className={css`
                animation: ${jackInTheBox} 1s ease-in;
            `}
            style={{
                content: {
                    backgroundColor: 'white',
                    overflow: 'visible',
                    borderRadius: 34,
                    border: 'none',
                    padding: 0,
                    top: 'initial',
                    bottom: 'initial',
                    left: 'initial',
                    right: 'initial',
                    maxWidth: 450,
                    position: 'relative',
                    outline: 'none'
                },
                overlay: {
                    backgroundColor: 'rgba(0,0,0,0.32)',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 20
                }
            }}>
            <div
                style={{
                    padding: 32,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                <h2
                    className={css`
                        font-weight: 400;
                        margin: 0 0 4px;
                        font-size: 18px;
                        color: #111111;
                    `}>
                    {widget.metadata.title || 'How are we doing?'}
                </h2>

                {widget.metadata.description ? (
                    <div style={{ fontSize: 14, marginBottom: 4 }}>
                        {widget.metadata.description}
                    </div>
                ) : null}

                <div
                    className={css`
                        display: flex;
                        justify-content: center;
                        flex-direction: row;
                    `}>
                    {range(5).map((ix) => (
                        <FontAwesome
                            weight={rating > ix ? 'solid' : 'regular'}
                            icon="star"
                            key={ix}
                            onClick={() => setRating(ix + 1)}
                            className={css`
                                color: #ffcb00;
                                font-size: 24px;
                                margin: 5px;
                                cursor: pointer;
                            `}
                        />
                    ))}
                </div>

                <label
                    className={css`
                        font-size: 12px;
                        margin: 8px 0;
                        color: ${({ theme }) =>
                            Color(theme.background).isLight() ? '#111111' : 'white'};
                    `}>
                    Leave a comment
                </label>
                <textarea
                    value={comment}
                    className={css`
                        border: 1px solid #d5d5d5;
                        outline: none;
                        height: 70px;
                    `}
                    onChange={(e) => setComment(e.target.value)}
                />

                <PopupSubmit onClick={submit}>Submit</PopupSubmit>
            </div>
            <div
                className={css`
                    opacity: 0;
                    animation: ${fadeIn} 1.2s ease-in;
                    animation-delay: 2s;
                    animation-fill-mode: both;
                `}
                onClick={() => setClosed(true)}
                style={{
                    display: 'flex',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    width: 30,
                    height: 30,
                    zIndex: 1000,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                }}>
                <FontAwesome icon="times" weight="light" style={{ fontSize: 18 }} />
            </div>
        </Modal>
    );
});

const Inline = forwardRef(function Inline(
    { widget, theme, vehicle, rating, setRating, submit, setComment, comment }: WidgetProps,
    ref: Ref<HTMLDivElement>
) {
    return (
        <div
            ref={ref}
            style={{
                textAlign: 'center',
                marginBottom: 8,
                padding: '0 16px',
                display: 'flex',
                flexDirection: 'column'
            }}>
            <h2
                className={css`
                    font-weight: 400;
                    margin: 0 0 4px;
                    font-size: 18px;
                    color: ${({ theme }) =>
                        Color(theme.background).isLight() ? '#111111' : 'white'};
                `}>
                {widget.metadata.title || 'How are we doing?'}
            </h2>

            {widget.metadata.description ? (
                <div style={{ fontSize: 14, marginBottom: 4 }}>{widget.metadata.description}</div>
            ) : null}

            <div
                className={css`
                    display: flex;
                    justify-content: center;
                    flex-direction: row;
                `}>
                {range(5).map((ix) => (
                    <FontAwesome
                        weight={rating > ix ? 'solid' : 'regular'}
                        icon="star"
                        key={ix}
                        onClick={() => setRating(ix + 1)}
                        className={css`
                            color: #ffcb00;
                            font-size: 24px;
                            margin: 5px;
                            cursor: pointer;
                        `}
                    />
                ))}
            </div>

            <label
                className={css`
                    font-size: 12px;
                    margin: 8px 0;
                    color: ${({ theme }) =>
                        Color(theme.background).isLight() ? '#111111' : 'white'};
                `}>
                Leave a comment
            </label>
            <textarea
                value={comment}
                className={css`
                    border: 1px solid #d5d5d5;
                    outline: none;
                    height: 70px;
                `}
                onChange={(e) => setComment(e.target.value)}
            />

            <InlineSubmit onClick={submit}>Submit</InlineSubmit>
        </div>
    );
});

export default observer(
    function Rating(
        { widget, theme, vehicle, job, host, auth }: WidgetProps,
        ref: Ref<HTMLDivElement>
    ) {
        const [rating, setRating] = useState(1);
        const [comment, setComment] = useState('');
        const [submitted, setSubmitted] = useState(false);

        function submit() {
            Tracking.track('Rating Submitted', { rating: rating / 5, comment });

            new Graph("prod-api.spatula.io").root
                .track(auth, job.id)
                .job()
                .ratings()
                .post([{ ratio: rating / 5, comment }], undefined)
                .then(() => {
                    setSubmitted(true);
                });
        }

        const RatingComponent = widget.settings && widget.settings.popup ? Popup : Inline;

        if (job.ratings.length > 0) return null;
        if (submitted) return null;

        return (
            <RatingComponent
                ref={ref}
                widget={widget}
                theme={theme}
                vehicle={vehicle}
                job={job}
                rating={rating}
                setRating={setRating}
                comment={comment}
                setComment={setComment}
                submit={submit}
                submitted={submitted}
            />
        );
    },
    {
        forwardRef: true
    }
);
